import axios from "axios";

import type { Certificate } from "@/types/certificate";
import type {
  BankAccount,
  Client,
  ClientAddress,
  ClientContact,
  ClientContactCreatePayload,
  ClientContactUpdatePayload,
  ClientCreatePayload,
  ClientUpdatePayload,
  ResponsibleLookup,
} from "@/types/client";

import { api, unwrap } from "./axiosConfig";

const base = (wsId: number) => `/api/v1/workspaces/${wsId}/clients`;

export const clientService = {
  async list(workspaceId: number, organizationId?: number): Promise<Client[]> {
    const params = organizationId != null ? { organization_id: organizationId } : undefined;
    return unwrap(api.get(base(workspaceId), { params }));
  },

  async get(workspaceId: number, clientId: number): Promise<Client> {
    return unwrap(api.get(`${base(workspaceId)}/${clientId}`));
  },

  async create(workspaceId: number, payload: ClientCreatePayload): Promise<Client> {
    return unwrap(api.post(base(workspaceId), payload));
  },

  async update(
    workspaceId: number,
    clientId: number,
    payload: ClientUpdatePayload,
  ): Promise<Client> {
    return unwrap(api.patch(`${base(workspaceId)}/${clientId}`, payload));
  },

  async remove(workspaceId: number, clientId: number): Promise<void> {
    await api.delete(`${base(workspaceId)}/${clientId}`);
  },

  async lookupResponsible(workspaceId: number, cpf: string): Promise<ResponsibleLookup> {
    const digits = cpf.replace(/\D/g, "");
    return unwrap(
      api.get(`${base(workspaceId)}/lookup-responsible`, { params: { cpf: digits } }),
    );
  },

  async setAddress(
    workspaceId: number,
    clientId: number,
    payload: ClientAddress,
  ): Promise<ClientAddress> {
    return unwrap(api.put(`${base(workspaceId)}/${clientId}/address`, payload));
  },

  async setBank(workspaceId: number, clientId: number, payload: BankAccount): Promise<BankAccount> {
    return unwrap(api.put(`${base(workspaceId)}/${clientId}/bank`, payload));
  },

  /** Certificado A1 ativo da empresa (null se não houver). */
  async getCertificate(workspaceId: number, clientId: number): Promise<Certificate | null> {
    try {
      return await unwrap(api.get(`${base(workspaceId)}/${clientId}/certificate`));
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 404) return null;
      throw err;
    }
  },

  async uploadCertificate(
    workspaceId: number,
    clientId: number,
    file: File,
    password: string,
  ): Promise<Certificate> {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("password", password);
    fd.append("accepted", "true");
    return unwrap(api.post(`${base(workspaceId)}/${clientId}/certificate`, fd));
  },

  async revokeCertificate(workspaceId: number, clientId: number): Promise<void> {
    await api.delete(`${base(workspaceId)}/${clientId}/certificate`);
  },
};

const contactsBase = (wsId: number, clientId: number) => `${base(wsId)}/${clientId}/contacts`;

export const clientContactService = {
  async list(workspaceId: number, clientId: number): Promise<ClientContact[]> {
    return unwrap(api.get(contactsBase(workspaceId, clientId)));
  },

  async create(
    workspaceId: number,
    clientId: number,
    payload: ClientContactCreatePayload,
  ): Promise<ClientContact> {
    return unwrap(api.post(contactsBase(workspaceId, clientId), payload));
  },

  async update(
    workspaceId: number,
    clientId: number,
    contactId: number,
    payload: ClientContactUpdatePayload,
  ): Promise<ClientContact> {
    return unwrap(api.patch(`${contactsBase(workspaceId, clientId)}/${contactId}`, payload));
  },

  async remove(workspaceId: number, clientId: number, contactId: number): Promise<void> {
    await api.delete(`${contactsBase(workspaceId, clientId)}/${contactId}`);
  },
};
